import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, KeyRound, Check, X, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const longEnough = pw.length >= 8;
  const matches = pw.length > 0 && pw === confirm;
  const canSubmit = longEnough && matches && !busy && !!token;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError("This reset link is missing its token. Please request a new one."); return; }
    if (!longEnough) { setError("Password must be at least 8 characters."); return; }
    if (!matches) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password reset. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const Req = ({ ok, children }) => (
    <li className={`flex items-center gap-1.5 ${ok ? "text-emerald-600" : "text-slate-400"}`}>
      {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />} {children}
    </li>
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl" data-testid="reset-password-screen">
        <div className="mb-6 flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <span className="font-display text-xl font-black text-slate-900">Watch<span className="text-blue-600">Relay</span></span>
        </div>

        <h1 className="font-display text-2xl font-black tracking-tight text-slate-900">Choose a new password</h1>
        <p className="mt-1 text-sm text-slate-500">Enter a new password for your WatchRelay account.</p>

        {!token && (
          <p data-testid="reset-missing-token" className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700 border border-amber-200">
            This link is invalid or incomplete. Please request a new reset link.
          </p>
        )}

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="rp-new" className="text-xs font-bold uppercase tracking-wider text-slate-500">New password</Label>
            <Input id="rp-new" data-testid="reset-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5" autoFocus />
          </div>
          <div>
            <Label htmlFor="rp-confirm" className="text-xs font-bold uppercase tracking-wider text-slate-500">Confirm new password</Label>
            <Input id="rp-confirm" data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" />
          </div>

          <ul className="space-y-1 text-xs">
            <Req ok={longEnough}>At least 8 characters</Req>
            <Req ok={matches}>Passwords match</Req>
          </ul>

          {error && <p data-testid="reset-error" className="text-sm font-medium text-red-600">{error}</p>}

          <Button type="submit" data-testid="reset-submit-button" disabled={!canSubmit} className="w-full gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold">
            <KeyRound className="h-4 w-4" /> {busy ? "Saving…" : "Reset password"}
          </Button>
        </form>

        <Link to="/login" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-400 hover:text-slate-600">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
